import { useTranslation } from "react-i18next";
// StepsHeroCard — Steptember MVP dashboard hero.
//
// Renders today's step count, a ring around a sneaker icon, a linear
// progress bar with "X steps to your goal", and a friend-ranking row
// (avatars + trophy + "#N of M friends").  Data:
//   - `/api/health/day` for today's steps + goal
//   - `/api/social/leaderboard?scope=friends&period=today` for ranking
//
// The card gracefully degrades: if the friends leaderboard is empty
// (e.g. brand-new account) we hide the ranking row so the card stays
// clean instead of showing "#0 of 0".
import { useEffect, useMemo, useState } from "react";
import { Footprints, Trophy } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/api";
import { resolveBackendUrl } from "@/lib/resolveBackendUrl";
const RING_SIZE = 108;
const RING_STROKE = 10;
const RING_RADIUS = (RING_SIZE - RING_STROKE) / 2;
const RING_CIRC = 2 * Math.PI * RING_RADIUS;

// Design token — brand cyan/mint from the mock.  Kept local so tweaking
// the hero glow doesn't ripple through global CSS.
const ACCENT = "#4CE0D7";
const ACCENT_DIM = "#4CE0D733";
function AvatarPill({
  item
}) {
  const {
    t
  } = useTranslation();
  if (item?.avatar_url) {
    return <img src={resolveBackendUrl(item.avatar_url)} alt={item.name || "friend"} className="h-7 w-7 rounded-full object-cover ring-2 ring-background -ml-2 first:ml-0" onError={e => {
      e.currentTarget.style.display = "none";
    }} />;
  }
  const letter = (item?.name || "?").charAt(0).toUpperCase();
  return <div className="h-7 w-7 rounded-full ring-2 ring-background -ml-2 first:ml-0
                        flex items-center justify-center bg-foreground text-background
                        text-[11px] font-semibold">
            {letter}
        </div>;
}
export default function StepsHeroCard() {
  const {
    t
  } = useTranslation();
  const nav = useNavigate();
  const [day, setDay] = useState(null);
  const [board, setBoard] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const {
          data
        } = await api.get("/health/day");
        if (alive) setDay(data);
      } catch {
        if (alive) setDay({});
      }
    })();
    (async () => {
      try {
        const {
          data
        } = await api.get("/social/leaderboard?scope=friends&period=today");
        if (alive) setBoard(data?.items || []);
      } catch {
        if (alive) setBoard([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);
  const steps = Number(day?.metrics?.steps?.value ?? day?.steps ?? 0) || 0;
  const goal = Number(day?.metrics?.steps?.goal ?? day?.goals?.steps ?? day?.step_goal ?? 10000) || 10000;
  const pct = goal > 0 ? Math.min(100, steps / goal * 100) : 0;
  const remain = Math.max(0, goal - steps);
  const dashOffset = RING_CIRC * (1 - pct / 100);
  const ranking = useMemo(() => {
    if (!board || board.length === 0) return null;
    const me = board.find(r => r.is_me);
    return me ? {
      rank: me.rank,
      total: board.length
    } : null;
  }, [board]);

  // Top 3 non-me friend avatars for the row.
  const preview = useMemo(() => {
    if (!board) return [];
    return board.filter(r => !r.is_me).slice(0, 3);
  }, [board]);
  return <div className="px-4 pt-3" data-testid="steps-hero-card">
            <button onClick={() => nav("/steps/history")} data-testid="steps-hero-card-btn" className="w-full text-left rounded-3xl border border-border/70 bg-card
                            px-5 py-5 relative overflow-hidden
                            active:scale-[0.99] transition" style={{
      background: "radial-gradient(120% 100% at 100% 0%, rgba(76,224,215,0.10) 0%, rgba(76,224,215,0) 55%), var(--card, #0B0F12)"
    }}>
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                        <p className="text-[10.5px] uppercase tracking-[0.14em] font-bold" style={{
            color: ACCENT
          }}>
                            {t("stepsHeroCard.today")}
                        </p>
                        <div className="mt-1 flex items-baseline gap-2">
                            <span className="font-display font-semibold tracking-tighter tabular-nums" style={{
              fontSize: 46,
              lineHeight: 1
            }} data-testid="steps-hero-count">
                                {steps.toLocaleString("nl-NL")}
                            </span>
                        </div>
                        <p className="text-[13px] font-semibold mt-1" style={{
            color: ACCENT
          }}>
                            steps
                        </p>
                    </div>

                    {/* Ring + sneaker */}
                    <div className="relative shrink-0" style={{
          width: RING_SIZE,
          height: RING_SIZE
        }}>
                        <svg width={RING_SIZE} height={RING_SIZE} className="-rotate-90">
                            <circle cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RING_RADIUS} fill="none" stroke={ACCENT_DIM} strokeWidth={RING_STROKE} />
                            <circle cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RING_RADIUS} fill="none" stroke={ACCENT} strokeWidth={RING_STROKE} strokeLinecap="round" strokeDasharray={RING_CIRC} strokeDashoffset={dashOffset} style={{
              transition: "stroke-dashoffset 700ms cubic-bezier(0.22,1,0.36,1)"
            }} />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <Footprints size={30} style={{
              color: ACCENT
            }} />
                        </div>
                    </div>
                </div>

                {/* Linear progress bar */}
                <div className="mt-4 h-2 rounded-full overflow-hidden" style={{
        background: "rgba(255,255,255,0.06)"
      }}>
                    <div className="h-full rounded-full" style={{
          width: `${pct}%`,
          background: ACCENT,
          transition: "width 700ms cubic-bezier(0.22,1,0.36,1)",
          boxShadow: `0 0 12px ${ACCENT_DIM}`
        }} />
                </div>
                <p className="text-[12.5px] mt-2" data-testid="steps-hero-remaining">
                    <span className="font-bold" style={{
          color: ACCENT
        }}>
                        {remain.toLocaleString("nl-NL")}
                    </span>
                    <span className="text-muted-foreground"> {t("stepsHeroCard.steps_to_your_goal")}</span>
                </p>

                {/* Friend ranking */}
                {ranking && <div className="mt-4 pt-4 border-t border-border/60
                                    flex items-center gap-3" data-testid="steps-hero-ranking">
                        <div className="flex">
                            {preview.map(p => <AvatarPill key={p.user_id} item={p} />)}
                        </div>
                        <div className="flex items-center gap-2 text-[13px] font-semibold">
                            <Trophy size={16} className="text-[#FFD60A]" />
                            <span className="text-[#FFD60A]">#{ranking.rank}</span>
                            <span className="text-muted-foreground">
                                {t("stepsHeroCard.of")} {ranking.total} friend{ranking.total === 1 ? "" : "s"}
                            </span>
                        </div>
                    </div>}
            </button>
        </div>;
}